import React, { useContext, useMemo } from 'react'
import { Tooltip, withStyles, IconButton, Typography, Box, Dialog, DialogTitle, DialogContent } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { Trans, useTranslation } from 'react-i18next'
import { formatToItalianCurrency } from '../../../utils/formatToItalianCurrency'
import { roundToNearestTenCents } from '../../../utils/roundToNearstTenCents'
import { Context } from '../../../Store'
import { BookingContext } from '../../../utils/BookingContext'
import { getCurrency, MAXIMUM_APEPAGO_LOAN_AMOUNT, PREMIUM_FEE_AMOUNT } from '../../../constants'
import { getPricing } from '../../../utils/getPricing'
import { useModal } from '../../../hooks/useModal'
import { isMobile } from 'react-device-detect'
import CloseIcon from '@material-ui/icons/Close'

const CustomTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: '#394555C7',
    opacity: '78%',
    borderRadius: 10,
    color: '#fff',
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 8,
    padding: '20px 16px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
  },
  arrow: {
    color: '#394555C7',
  },
}))(Tooltip)

function EasyPayPlanTooltipDividerLine() {
  return (
    <Box
      style={{
        borderBottom: isMobile ? '1px solid #00000008' : '1px solid #FFFFFF21',
        opacity: '3%',
        marginBottom: isMobile ? '22px' : '20px',
      }}
    />
  )
}

const EasyPayPlanTooltipContent = () => {
  const { t } = useTranslation()
  const [state] = useContext(Context)
  const toCurrency = (amount) => formatToItalianCurrency(roundToNearestTenCents(amount))
  const { hasCounteroffer } = useContext(BookingContext)
  const { value: propertyCurrency } = getCurrency(state.property.currency)
  const { upfrontFee, deposit } = useMemo(() => getPricing(state.property, state.booking, hasCounteroffer), [
    state.property,
    state.booking,
    hasCounteroffer,
  ])
  const upfontfeePlusDeposit = upfrontFee + deposit
  const baseLoanable = Math.min(upfrontFee + deposit, MAXIMUM_APEPAGO_LOAN_AMOUNT)
  return (
    <Box>
      {!isMobile && (
        <>
          <Typography style={{ fontSize: '16px', fontWeight: 600, marginBottom: '10px', color: isMobile ? '#000000' : '#fff' }}>
            {t('easyPayPlan.tooltip.title')}
          </Typography>
          <EasyPayPlanTooltipDividerLine />
        </>
      )}
      <Typography style={{ fontSize: '14px', fontWeight: 400, marginBottom: '10px', color: isMobile ? '#394555' : '#fff' }}>
        <Trans
          i18nKey="easyPayPlan.tooltip.tip_1"
          components={{
            1: <span style={{ fontWeight: 700 }} />,
          }}
          values={{
            comission_plus_deposit_amount: toCurrency(upfontfeePlusDeposit),
            currency: propertyCurrency,
          }}
        />
      </Typography>
      <Typography style={{ fontSize: '14px', fontWeight: 400, marginBottom: '10px', color: isMobile ? '#394555' : '#fff' }}>
        <Trans
          i18nKey="easyPayPlan.tooltip.tip_2"
          components={{
            1: <span style={{ fontWeight: 700, color: isMobile ? '#0C9D9D' : '#fff' }} />,
            2: <span style={{ fontWeight: 700 }} />,
          }}
          values={{
            easy_pay_amount: toCurrency(baseLoanable),
            currency: propertyCurrency,
          }}
        />
      </Typography>
      <Typography style={{ fontSize: '14px', fontWeight: 400, marginBottom: '10px', color: isMobile ? '#394555' : '#fff' }}>
        <Trans
          i18nKey="easyPayPlan.tooltip.tip_3"
          components={{
            1: <span style={{ fontWeight: 700 }} />,
          }}
          values={{
            easypay_total_amount: toCurrency(baseLoanable + PREMIUM_FEE_AMOUNT),
            currency: propertyCurrency,
            premium_fee_amount: toCurrency(PREMIUM_FEE_AMOUNT),
          }}
        />
      </Typography>
      <Typography style={{ fontSize: '14px', fontWeight: 400, marginBottom: '10px', color: isMobile ? '#394555' : '#fff' }}>
        <Trans
          i18nKey="easyPayPlan.tooltip.disclaimer"
          values={{
            easypay_remaining_amount: toCurrency(Math.max(0, upfrontFee + deposit - MAXIMUM_APEPAGO_LOAN_AMOUNT)),
            currency: propertyCurrency,
          }}
        />
      </Typography>
    </Box>
  )
}

export const EasyPayPlanTooltipDialog = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { borderRadius: '20px', boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)', padding: '20px 16px 39px 16px' },
      }}
    >
      <DialogTitle disableTypography style={{ padding: 0 }}>
        <Box
          style={{
            position: 'relative',
            textAlign: 'flex-start',
          }}
        >
          <Typography
            style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#000000',
              margin: 0,
            }}
          >
            {t('easyPayPlan.tooltip.title')}
          </Typography>
          <EasyPayPlanTooltipDividerLine />
          <IconButton
            aria-label="close"
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            size="small"
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: 0 }}>
        <EasyPayPlanTooltipContent />
      </DialogContent>
    </Dialog>
  )
}

export default function EasyPayPlanTooltip() {
  const { isOpen, onOpen, onClose } = useModal()
  return (
    <>
      <EasyPayPlanTooltipDialog isOpen={isOpen} onClose={onClose} />
      <CustomTooltip title={<EasyPayPlanTooltipContent />} placement="auto" arrow>
        <IconButton
          style={{ width: 12, height: 12, marginBottom: 18 }}
          onClick={(e) => {
            e.stopPropagation()
            isMobile && onOpen()
          }}
        >
          <InfoOutlinedIcon style={{ fontSize: 12, color: '#0C9D9D' }} />
        </IconButton>
      </CustomTooltip>
    </>
  )
}
